import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Chip, CircularProgress, Card, CardContent,
  IconButton, Collapse, Stepper, Step, StepLabel, Alert, Button, Divider,
} from '@mui/material';
import {
  BugReport as BugIcon, Lightbulb as FeatureIcon, Tune as ImprovementIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon, CheckCircle as ApproveIcon,
  RateReview as ReviewIcon,
} from '@mui/icons-material';
import Markdown from 'react-markdown';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import PageHeader from '../../ui-components/PageHeader';

const TYPE_CONFIG = {
  bug: { label: 'Bug', icon: <BugIcon />, color: '#ef4444', bg: '#fef2f2' },
  feature: { label: 'Nieuw idee', icon: <FeatureIcon />, color: '#f59e0b', bg: '#fffbeb' },
  improvement: { label: 'Verbetering', icon: <ImprovementIcon />, color: '#6366f1', bg: '#eef2ff' },
};

const PRIORITY_COLORS = {
  low: { label: 'Laag', color: '#64748b' },
  medium: { label: 'Normaal', color: '#3b82f6' },
  high: { label: 'Hoog', color: '#ef4444' },
};

const PIPELINE_STEPS = [
  { key: 'new', label: 'Nieuw' },
  { key: 'plan', label: 'Plan' },
  { key: 'validatie', label: 'Check' },
  { key: 'review', label: 'Review' },
  { key: 'implementatie', label: 'Bouwen' },
  { key: 'pr_open', label: 'Op test' },
  { key: 'merged_to_develop', label: 'Testen' },
  { key: 'done', label: 'Live' },
];

const PIPELINE_STEPS_WEBSITE = [
  { key: 'new', label: 'Nieuw' },
  { key: 'plan', label: 'Plan' },
  { key: 'validatie', label: 'Check' },
  { key: 'review', label: 'Review' },
  { key: 'implementatie', label: 'Bouwen' },
  { key: 'pr_open', label: 'Op test' },
  { key: 'merged_to_main', label: 'Live' },
  { key: 'done', label: 'Live' },
];

const STATUS_TO_STEP = {
  new: 0,
  plan_started: 1, plan_created: 1, plan_failed: 1,
  validation_started: 2, validation_approved: 2, validation_rejected: 2,
  awaiting_review: 3,
  implementation_started: 4,
  pr_open: 5, pr_failed: 5,
  merged_to_develop: 6, deployed_to_test: 6,
  approved_for_prod: 7, merged_to_main: 7,
  done: 7,
};

const STATUS_TO_STEP_WEBSITE = {
  new: 0,
  plan_started: 1, plan_created: 1, plan_failed: 1,
  validation_started: 2, validation_approved: 2, validation_rejected: 2,
  awaiting_review: 3,
  implementation_started: 4,
  pr_open: 5, pr_failed: 5,
  merged_to_main: 6,
  done: 7,
};

const PIPELINE_STATUS_LABELS = {
  new: {
    label: 'Nieuw', color: '#94a3b8',
    explain: 'Je feedback is ontvangen en wordt binnenkort opgepakt.',
    nextStep: 'Je hoeft niets te doen. De automatische verwerking start binnen enkele minuten.',
  },
  plan_started: {
    label: 'Plan bezig...', color: '#a78bfa',
    explain: 'AI genereert een implementatieplan voor je feedback.',
    nextStep: 'Even geduld — dit duurt meestal 1-2 minuten.',
  },
  plan_created: {
    label: 'Plan klaar', color: '#8b5cf6',
    explain: 'Het plan is klaar en wordt gevalideerd.',
    nextStep: 'Het plan wordt automatisch gecontroleerd op veiligheid. Dit duurt enkele seconden.',
  },
  plan_failed: {
    label: 'Plan mislukt', color: '#ef4444',
    explain: 'Het lukte niet om een plan te genereren. Dit kan komen door complexiteit of technische beperkingen.',
    nextStep: 'Het team bekijkt dit handmatig. Je kunt ook extra details toevoegen door opnieuw feedback in te dienen met meer context.',
  },
  validation_started: {
    label: 'Validatie bezig...', color: '#60a5fa',
    explain: 'Het plan wordt gecontroleerd op veiligheid en haalbaarheid.',
    nextStep: 'Dit gaat automatisch — je hoeft niets te doen.',
  },
  validation_approved: {
    label: 'Validatie OK', color: '#3b82f6',
    explain: 'Het plan is goedgekeurd en klaar voor review.',
    nextStep: 'Je krijgt zo een verzoek om het plan te bekijken en goed te keuren.',
  },
  validation_rejected: {
    label: 'Validatie afgekeurd', color: '#ef4444',
    explain: 'Het plan voldoet niet aan de veiligheidsregels en kan niet worden uitgevoerd.',
    nextStep: 'Het team bekijkt of het handmatig opgelost kan worden. Je hoeft niets te doen.',
  },
  awaiting_review: {
    label: 'Wordt beoordeeld', color: '#f59e0b',
    explain: 'Het plan wordt beoordeeld door het team voordat het gebouwd wordt.',
    nextStep: 'Je hoeft niets te doen. Het team bekijkt het plan en geeft het vrij zodra het in orde is.',
  },
  implementation_started: {
    label: 'Wordt gebouwd...', color: '#f59e0b',
    explain: 'AI implementeert de wijzigingen op basis van het goedgekeurde plan.',
    nextStep: 'Even geduld — dit duurt meestal 3-5 minuten. Je wordt automatisch bijgewerkt.',
  },
  pr_open: {
    label: 'Op test', color: '#f59e0b',
    explain: 'De wijzigingen staan op de testomgeving.',
    nextStep: 'Test de wijzigingen op test.fullframe.nl. Werkt het? Laat het weten!',
  },
  pr_failed: {
    label: 'Bouwen mislukt', color: '#ef4444',
    explain: 'De implementatie is niet gelukt. Dit kan komen door conflicten met recente wijzigingen.',
    nextStep: 'Het team bekijkt dit. Je kunt eventueel extra context geven door opnieuw feedback in te dienen.',
  },
  merged_to_develop: {
    label: 'Wordt getest', color: '#3b82f6',
    explain: 'De wijzigingen zijn samengevoegd en worden op test gecontroleerd.',
    nextStep: 'Je kunt de wijzigingen testen op test.fullframe.nl. Klopt alles? Dan gaat het naar productie.',
  },
  deployed_to_test: {
    label: 'Op test', color: '#06b6d4',
    explain: 'De wijzigingen staan live op de testomgeving.',
    nextStep: 'Test het op test.fullframe.nl en geef aan of het werkt zoals verwacht.',
  },
  approved_for_prod: {
    label: 'Goedgekeurd', color: '#10b981',
    explain: 'Goedgekeurd voor productie.',
    nextStep: 'De wijzigingen worden binnenkort live gezet op intern.fullframe.nl.',
  },
  merged_to_main: {
    label: 'Live', color: '#059669',
    explain: 'De wijzigingen zijn live op productie.',
    nextStep: 'Alles is verwerkt! Check het resultaat op intern.fullframe.nl.',
  },
  done: {
    label: 'Afgerond', color: '#22c55e',
    explain: 'Je feedback is volledig verwerkt en live.',
    nextStep: null,
  },
  rejected: {
    label: 'Afgewezen', color: '#ef4444',
    explain: 'Deze feedback is afgewezen en wordt niet verwerkt.',
    nextStep: 'Als je denkt dat dit onterecht is, kun je opnieuw feedback indienen met meer context.',
  },
};

const FAILED = ['plan_failed', 'pr_failed', 'validation_rejected', 'rejected'];
const IN_PROGRESS = ['plan_started', 'plan_created', 'validation_started', 'validation_approved', 'awaiting_review', 'implementation_started'];

function getPipelineStep(status, targetRepo) {
  if (targetRepo === 'websiteff') return STATUS_TO_STEP_WEBSITE[status] ?? 0;
  return STATUS_TO_STEP[status] ?? 0;
}

function getPipelineSteps(targetRepo) {
  return targetRepo === 'websiteff' ? PIPELINE_STEPS_WEBSITE : PIPELINE_STEPS;
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/**
 * User-friendly plan summary — extracts the key info from the raw markdown
 * and presents it in a way that makes sense to non-technical users.
 */
function PlanSummaryCard({ planSummary, validationResult, isReview }) {
  const [showDetails, setShowDetails] = useState(false);

  // Extract key info from the plan markdown
  const extractSummary = (md) => {
    if (!md) return null;
    // Find the "Samenvatting" section
    const summaryMatch = md.match(/##?\s*Samenvatting[^\n]*\n([\s\S]*?)(?=\n##?\s|\n---|\Z)/i);
    // Find "Wat er gewijzigd moet worden" or similar
    const changesMatch = md.match(/##?\s*Wat er (?:gewijzigd|aangepast|veranderd)[^\n]*\n([\s\S]*?)(?=\n##?\s|\n---|\Z)/i);
    // Find "Niet uitvoerbaar" or "feasible: false"
    const notFeasible = md.toLowerCase().includes('niet uitvoerbaar') || md.toLowerCase().includes('niet haalbaar');
    // Find file count
    const filesMatch = md.match(/Bestanden:\s*(\d+)/i);
    const linesMatch = md.match(/Regels[^:]*:\s*~?(\d+)/i);

    return {
      summary: summaryMatch?.[1]?.trim()?.split('\n')[0] || null,
      hasChanges: !!changesMatch,
      notFeasible,
      files: filesMatch?.[1] || null,
      lines: linesMatch?.[1] || null,
    };
  };

  const info = extractSummary(planSummary);
  const isApproved = validationResult?.toLowerCase().includes('approved');
  const needsChanges = validationResult?.toLowerCase().includes('approved_with_changes');
  const isRejected = validationResult?.toLowerCase().includes('rejected') && !validationResult?.toLowerCase().includes('approved');

  return (
    <Box sx={{ mt: 2 }}>
      {/* Simple user-facing summary */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: info?.notFeasible ? '#fef2f2' : '#f8fafc' }}>
        {info?.notFeasible ? (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#ef4444', mb: 0.5 }}>
              Kan niet automatisch worden opgelost
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {info.summary || 'Er is te weinig informatie om dit automatisch te verwerken, of het probleem is te complex voor de automatische pipeline.'}
            </Typography>
            <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#fff', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569' }}>Wat kun je doen?</Typography>
              <Typography variant="body2" sx={{ mt: 0.3, fontSize: '0.82rem' }}>
                Dien opnieuw feedback in met meer details: beschrijf precies wat je deed, wat er gebeurde, en wat je verwachtte. Een screenshot met de foutmelding helpt enorm.
              </Typography>
            </Box>
          </>
        ) : isRejected ? (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#ef4444', mb: 0.5 }}>
              Plan afgekeurd
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Het voorgestelde plan voldoet niet aan de veiligheidsregels en kan niet automatisch worden uitgevoerd.
            </Typography>
            <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#fff', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569' }}>Wat kun je doen?</Typography>
              <Typography variant="body2" sx={{ mt: 0.3, fontSize: '0.82rem' }}>
                Het team bekijkt dit handmatig. Je hoeft niets te doen — als er vragen zijn nemen we contact op.
              </Typography>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#6366f1', mb: 0.5 }}>
              {isReview ? 'Plan klaar — jouw goedkeuring nodig' : 'Plan opgesteld'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {info?.summary || 'Er is een plan opgesteld om je feedback te verwerken.'}
              {info?.files && ` (${info.files} bestanden, ~${info.lines || '?'} regels)`}
            </Typography>
            {needsChanges && (
              <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: '#d97706' }}>
                Het plan is goedgekeurd met kleine aanpassingen.
              </Typography>
            )}
          </>
        )}

        {/* Toggle for technical details */}
        <Typography
          variant="caption"
          onClick={(e) => { e.stopPropagation(); setShowDetails(!showDetails); }}
          sx={{ mt: 1, display: 'inline-block', color: '#3b82f6', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
        >
          {showDetails ? 'Verberg technische details' : 'Toon technische details'}
        </Typography>

        <Collapse in={showDetails}>
          <Divider sx={{ my: 1.5 }} />
          {planSummary && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>Implementatieplan</Typography>
              <Box sx={{ '& p': { margin: '4px 0', fontSize: '0.8rem' }, '& h1,& h2,& h3': { fontSize: '0.85rem', mt: 1, mb: 0.3 }, '& ul,& ol': { pl: 2, fontSize: '0.8rem' }, '& li': { mb: 0.2 } }}>
                <Markdown>{planSummary}</Markdown>
              </Box>
            </Box>
          )}
          {validationResult && (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>Validatie resultaat</Typography>
              <Box sx={{ '& p': { margin: '4px 0', fontSize: '0.8rem' }, '& h1,& h2,& h3': { fontSize: '0.85rem', mt: 1, mb: 0.3 }, '& ul,& ol': { pl: 2, fontSize: '0.8rem' } }}>
                <Markdown>{validationResult}</Markdown>
              </Box>
            </Box>
          )}
        </Collapse>
      </Paper>
    </Box>
  );
}

export default function MyFeedback() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [approving, setApproving] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/feedback/my?limit=100`);
      if (res.ok) {
        const data = await res.json();
        setItems(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('[MyFeedback] Error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh when items are in progress or awaiting review
  useEffect(() => {
    const hasRunning = items.some(i => IN_PROGRESS.includes(i.pipeline_status));
    if (!hasRunning) return;
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [items, fetchData]);

  // Auto-expand item that needs review
  useEffect(() => {
    const needsReview = items.find(i => i.pipeline_status === 'awaiting_review');
    if (needsReview && expandedId !== needsReview.id) {
      setExpandedId(needsReview.id);
    }
  }, [items, expandedId]);

  const handleApprove = async (id) => {
    setApproving(id);
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/feedback/${id}/user-approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      toast.success('Plan goedgekeurd — implementatie gestart!');
      fetchData();
    } catch (err) {
      toast.error(`Goedkeuring mislukt: ${err.message}`);
    } finally {
      setApproving(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', maxWidth: 1100, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
        <PageHeader icon="feedback" title="Mijn Feedback" subtitle="Status van jouw ingediende feedback" />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      </Box>
    );
  }

  // Sort: awaiting_review first, then in_progress, then by date
  const sorted = [...items].sort((a, b) => {
    if (a.pipeline_status === 'awaiting_review' && b.pipeline_status !== 'awaiting_review') return -1;
    if (b.pipeline_status === 'awaiting_review' && a.pipeline_status !== 'awaiting_review') return 1;
    if (IN_PROGRESS.includes(a.pipeline_status) && !IN_PROGRESS.includes(b.pipeline_status)) return -1;
    if (IN_PROGRESS.includes(b.pipeline_status) && !IN_PROGRESS.includes(a.pipeline_status)) return 1;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const reviewCount = items.filter(i => i.pipeline_status === 'awaiting_review').length;

  return (
    <Box sx={{ width: '100%', maxWidth: 1100, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <PageHeader
        icon="feedback"
        title="Mijn Feedback"
        subtitle={`${items.length} item${items.length !== 1 ? 's' : ''}${reviewCount > 0 ? ` — ${reviewCount} wacht op review` : ''}`}
        rightActions={
          <IconButton onClick={() => { setLoading(true); fetchData(); }}><RefreshIcon /></IconButton>
        }
      />

      {items.length === 0 && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Je hebt nog geen feedback ingediend. Gebruik de feedback-knop (linksonder) om te beginnen.
          </Typography>
        </Paper>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {sorted.map((item) => {
          const type = TYPE_CONFIG[item.feedback_type] || TYPE_CONFIG.improvement;
          const prio = PRIORITY_COLORS[item.priority] || PRIORITY_COLORS.medium;
          const pipelineStatus = item.pipeline_status || 'new';
          const statusInfo = PIPELINE_STATUS_LABELS[pipelineStatus] || PIPELINE_STATUS_LABELS.new;
          const isExpanded = expandedId === item.id;
          const isFailed = FAILED.includes(pipelineStatus);
          const isRunning = IN_PROGRESS.includes(pipelineStatus);
          const isDone = pipelineStatus === 'done';
          const isReview = pipelineStatus === 'awaiting_review';
          const targetRepo = item.target_repo || 'ff-material-app';

          return (
            <Card key={item.id} variant="outlined" onClick={() => setExpandedId(isExpanded ? null : item.id)} sx={{
              borderLeft: `4px solid ${isReview ? '#f59e0b' : type.color}`,
              cursor: 'pointer',
              transition: 'box-shadow 0.15s',
              '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
              ...(isFailed && { borderColor: '#ef4444' }),
              ...(isDone && { borderColor: '#22c55e' }),
              ...(isRunning && { boxShadow: '0 0 0 1px rgba(99,102,241,0.3)', animation: 'pulse 2s infinite' }),
              ...(isReview && { boxShadow: '0 0 0 2px rgba(245,158,11,0.4)', bgcolor: '#fffbeb' }),
            }}>
              <CardContent sx={{ pb: '12px !important' }}>
                {/* Review banner */}
                {isReview && (
                  <Alert severity="warning" icon={<ReviewIcon />} sx={{ mb: 1.5, fontSize: '0.85rem' }}>
                    <strong>Jouw review nodig</strong> — bekijk het plan hieronder en keur goed of geef aanpassingen door.
                  </Alert>
                )}

                {/* Header row */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                  {React.cloneElement(type.icon, { sx: { color: type.color, fontSize: 20, mt: 0.3 } })}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.description?.slice(0, 120)}{item.description?.length > 120 ? '...' : ''}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Chip label={item.tool_name} size="small" sx={{ bgcolor: '#e0e7ff', color: '#4338ca', fontSize: '0.7rem', height: 22 }} />
                      <Chip label={statusInfo.label} size="small"
                        sx={{ bgcolor: `${statusInfo.color}15`, color: statusInfo.color, fontWeight: 600, fontSize: '0.7rem', height: 22 }} />
                      {targetRepo === 'websiteff' && <Chip label="🌐 Website" size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 22 }} />}
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>{formatDate(item.created_at)}</Typography>
                    </Box>
                  </Box>
                  <IconButton size="small" onClick={() => setExpandedId(isExpanded ? null : item.id)}>
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                {/* Pipeline stepper */}
                {item.ai_eligible && (
                  <Box sx={{ mt: 1.5, mx: -1 }}>
                    <Stepper activeStep={getPipelineStep(pipelineStatus, targetRepo)} alternativeLabel
                      sx={{
                        '& .MuiStepLabel-label': { fontSize: '0.6rem' },
                        '& .MuiStepIcon-root': { fontSize: '1rem' },
                        '& .MuiStepIcon-root.Mui-active': { color: isReview ? '#f59e0b' : isRunning ? '#6366f1' : isFailed ? '#ef4444' : undefined },
                        '& .MuiStepIcon-root.Mui-completed': { color: '#22c55e' },
                      }}>
                      {getPipelineSteps(targetRepo).map((s, i) => {
                        const stepIdx = getPipelineStep(pipelineStatus, targetRepo);
                        const isFailedStep = isFailed && i === stepIdx;
                        return (
                          <Step key={s.key} sx={isFailedStep ? { '& .MuiStepIcon-root': { color: '#ef4444 !important' } } : {}}>
                            <StepLabel error={isFailedStep}>{s.label}</StepLabel>
                          </Step>
                        );
                      })}
                    </Stepper>
                  </Box>
                )}

                {/* Status explanation + next step — always visible for non-new items */}
                {pipelineStatus !== 'new' && (
                  <Box sx={{ mt: 1, px: 1 }}>
                    {isFailed ? (
                      <Alert severity="error" sx={{ fontSize: '0.8rem', py: 0.5 }}>
                        <strong>{statusInfo.label}:</strong> {item.error_detail || statusInfo.explain}
                        {statusInfo.nextStep && (
                          <Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                            <strong>Wat nu?</strong> {statusInfo.nextStep}
                          </Typography>
                        )}
                      </Alert>
                    ) : (isRunning || isReview) ? (
                      <Box>
                        <Typography variant="caption" sx={{ color: statusInfo.color, fontStyle: 'italic' }}>
                          {statusInfo.explain}
                        </Typography>
                        {statusInfo.nextStep && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.3, color: '#475569', fontWeight: 500 }}>
                            → {statusInfo.nextStep}
                          </Typography>
                        )}
                      </Box>
                    ) : statusInfo.nextStep ? (
                      <Typography variant="caption" sx={{ color: '#475569' }}>
                        → {statusInfo.nextStep}
                      </Typography>
                    ) : null}
                  </Box>
                )}

                {/* User-friendly plan summary */}
                {(isReview || isExpanded) && item.plan_summary && (
                  <PlanSummaryCard planSummary={item.plan_summary} validationResult={item.validation_result} isReview={isReview} />
                )}

                {/* Review action buttons */}
                {isReview && (
                  <Box sx={{ mt: 2, display: 'flex', gap: 1.5 }}>
                    <Button variant="contained" startIcon={<ApproveIcon />}
                      onClick={() => handleApprove(item.id)}
                      disabled={approving === item.id}
                      sx={{ textTransform: 'none', bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' }, borderRadius: 2 }}>
                      {approving === item.id ? 'Bezig...' : 'Goedkeuren — implementeren'}
                    </Button>
                  </Box>
                )}

                {/* Expanded details */}
                <Collapse in={isExpanded && !isReview}>
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e2e8f0' }}>
                    <Typography variant="body2" sx={{ mb: 1.5, whiteSpace: 'pre-wrap' }}>{item.description}</Typography>

                    {item.follow_up_answers && Object.keys(item.follow_up_answers).length > 0 && (
                      <Box sx={{ mb: 1.5 }}>
                        {Object.entries(item.follow_up_answers).map(([key, val]) => val && (
                          <Box key={key} sx={{ mb: 0.5 }}>
                            <Typography variant="caption" sx={{ color: '#6366f1', fontWeight: 600 }}>{key.replace(/_/g, ' ')}</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{val}</Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {item.screenshot_urls?.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                        {item.screenshot_urls.map((url, i) => (
                          <Box key={i} component="a" href={url} target="_blank" rel="noopener"
                            sx={{ display: 'block', width: 100, height: 75, borderRadius: 1, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                            <img src={url} alt={`Screenshot ${i+1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          </Box>
                        ))}
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Chip label={type.label} size="small" icon={React.cloneElement(type.icon, { sx: { fontSize: 14 } })} sx={{ bgcolor: type.bg, color: type.color }} />
                      <Chip label={prio.label} size="small" sx={{ color: prio.color, borderColor: prio.color }} variant="outlined" />
                      {item.ai_eligible && <Chip label="🤖 AI verwerkt" size="small" variant="outlined" />}
                    </Box>
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      <style>{`
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 1px rgba(99,102,241,0.3); }
          50% { box-shadow: 0 0 0 3px rgba(99,102,241,0.15); }
        }
      `}</style>
    </Box>
  );
}
